import React, { useState, useEffect } from 'react'
import ThumbsUpDownRoundedIcon from '@mui/icons-material/ThumbsUpDownRounded'
import { Modal, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PrintIcon from '@mui/icons-material/Print'
import CitationViewer from './CitationViewer'
import { PDFDocument } from 'pdf-lib'
import useModal from 'src/hooks/useModal'
import Sentry from 'src/lib/sentry'

type CitationViewerModalProps = {
  isOpen: boolean
  maintenanceItemId?: string
  reference: {
    id: string
    name: string
    referenceType: string
    entityType: string
    entityId: string
    pageNumbers?: string
    excerptFile?: {
      id: string
      url: string
    }
    referenceSource?: {
      id: string
      url: string
    }
  } | null
  onClose: (event: React.MouseEvent) => void
}

const CitationViewerModal = ({
  isOpen,
  onClose,
  reference,
  maintenanceItemId,
}: CitationViewerModalProps) => {
  const [displayPdfUrl, setDisplayPdfUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { handleOpen: openReferenceFeedbackModal } = useModal(
    'referenceFeedbackModal'
  )

  useEffect(() => {
    if (!isOpen) {
      setDisplayPdfUrl(null)
      return
    }

    const abortController = new AbortController()
    let isMounted = true

    const loadPdf = async () => {
      if (!reference) {
        setDisplayPdfUrl(null)
        return
      }

      setIsLoading(true)
      try {
        // Case 1: Use excerptFile URL if available
        if (reference.excerptFile?.url) {
          setDisplayPdfUrl(reference.excerptFile.url)
          return
        }

        // Case 2: Use referenceSource URL with page range
        if (reference.referenceSource?.url && reference.pageNumbers) {
          await createExtractedPDF(
            parsePageRange(reference.pageNumbers),
            abortController.signal,
            reference.referenceSource.url
          )
          return
        }

        // Case 3: Neither case is possible
        throw new Error('No valid PDF source available')
      } catch (error) {
        console.error('Error loading PDF:', error)
        setDisplayPdfUrl(null)
      } finally {
        setIsLoading(false)
      }
    }

    loadPdf()

    return () => {
      abortController.abort()
      isMounted = false
    }
  }, [reference, isOpen])

  // Cleanup function to revoke object URLs
  useEffect(() => {
    return () => {
      if (displayPdfUrl && displayPdfUrl !== reference?.excerptFile?.url) {
        URL.revokeObjectURL(displayPdfUrl)
      }
    }
  }, [displayPdfUrl, reference])

  useEffect(() => {
    // If for whatever reason we dont have a pdf url, send a warning to sentry so we can investigate
    if (isOpen && displayPdfUrl === null && !isLoading) {
      try {
        Sentry.setContext('CitationViewerModal', {
          reference,
          maintenanceItemId,
          isOpen,
          displayPdfUrl,
          isLoading,
        })
        Sentry.captureMessage(
          'CitationViewerModal: displayPdfUrl is null when modal is rendered',
          'warning'
        )
      } catch (e) {
        console.error('Error sending warning to sentry', e)
      }
    }
  }, [isOpen, displayPdfUrl, isLoading, reference, maintenanceItemId])

  function parsePageRange(input: string): number[] {
    const parts = input.split(',').flatMap((part) => {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(Number)
        return Array.from({ length: end - start + 1 }, (_, i) => start + i)
      }
      return [Number(part)]
    })
    return [...new Set(parts)].sort((a, b) => a - b)
  }

  const createExtractedPDF = async (
    range: number[],
    signal?: AbortSignal,
    sourceUrl: string
  ) => {
    if (!range.length) return

    try {
      // Fetch the original PDF
      const response = await fetch(sourceUrl, { signal })
      const pdfBytes = await response.arrayBuffer()

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(pdfBytes)
      const newPdfDoc = await PDFDocument.create()

      // Copy the selected pages
      const copiedPages = await newPdfDoc.copyPages(
        pdfDoc,
        range.map((page) => page - 1)
      )
      copiedPages.forEach((page) => newPdfDoc.addPage(page))

      // Save the new PDF
      const newPdfBytes = await newPdfDoc.save()

      // Create a blob URL for the new PDF
      const blob = new Blob([newPdfBytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)

      // Only update state if component is still mounted
      if (!signal?.aborted) {
        setDisplayPdfUrl(url)
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('PDF extraction aborted')
      } else {
        console.error('Error creating PDF:', error)
      }
    }
  }

  // Print handler
  const handlePrint = () => {
    if (!displayPdfUrl) return
    const printWindow = window.open(displayPdfUrl)
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus()
        printWindow.print()
      }
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="citation-viewer-modal"
    >
      <div className="absolute left-1/2 top-1/2 flex max-h-[90vh] w-[70vw] min-w-[600px] -translate-x-1/2 -translate-y-1/2 transform flex-col rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b p-4">
          <Typography variant="h6" component="h2">
            {reference?.name || 'Document Viewer'}
          </Typography>
          <div className="flex gap-2">
            <IconButton
              disabled={!maintenanceItemId}
              onClick={(event) => {
                onClose(event)
                openReferenceFeedbackModal({
                  reference,
                  maintenanceItemId,
                })
              }}
              size="small"
              aria-label="Rate"
            >
              <ThumbsUpDownRoundedIcon />
            </IconButton>
            <IconButton onClick={handlePrint} size="small" aria-label="Print">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
          {displayPdfUrl === null && !isLoading ? (
            <Typography color="error" className="mt-8 text-center">
              Sorry, we were unable to display this document. Please contact
              support if you believe this is an error.
            </Typography>
          ) : (
            <CitationViewer
              fileUrl={displayPdfUrl}
              onClose={onClose}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}

export default CitationViewerModal
